import { formatSEK } from '@budgie/shared';
import { ChevronDownIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { RouterOutputs } from '../../lib/trpc';
import { shortDayLabel } from './dates';

export type PurchaseItem = RouterOutputs['transaction']['search']['items'][number];

/** Ledger amount: exact öre, inflows get a plus sign. */
export function formatAmount(ore: number) {
  return ore > 0 ? `+${formatSEK(ore)}` : formatSEK(ore);
}

/** Transfers and excluded rows don't count toward the budget, so they recede. */
export function isMuted(row: Pick<PurchaseItem, 'isTransfer' | 'isExcluded'>) {
  return row.isTransfer || row.isExcluded;
}

export function amountClass(row: Pick<PurchaseItem, 'amount' | 'isTransfer' | 'isExcluded'>) {
  if (isMuted(row)) return 'text-muted-foreground';
  return row.amount > 0 ? 'text-positive' : 'text-foreground';
}

function Tag({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'warning' }) {
  return (
    <span
      className={cn(
        'inline-flex h-5 max-w-40 items-center rounded-md px-1.5 text-[11px] font-medium',
        tone === 'warning' ? 'bg-warning-soft text-warning' : 'bg-muted text-muted-foreground',
      )}
    >
      <span className="truncate">{children}</span>
    </span>
  );
}

/** Linked bill/loan/income plus status flags for a transaction. */
export function PurchaseTags({ row }: { row: PurchaseItem }) {
  return (
    <>
      {row.billName && <Tag>{row.billName}</Tag>}
      {row.loanName && <Tag>{row.loanName}</Tag>}
      {row.incomeName && <Tag>{row.incomeName}</Tag>}
      {row.isTransfer && <Tag>Överföring</Tag>}
      {row.isExcluded && <Tag>Räknas inte</Tag>}
      {row.isHidden && <Tag>Dold</Tag>}
      {row.isRecurring && <Tag>Återkommande</Tag>}
      {row.needsReview && <Tag tone="warning">Granska</Tag>}
    </>
  );
}

/** Tappable category chip that opens the category picker. */
export function CategoryChip({
  categoryName,
  groupName,
  onClick,
  disabled,
}: {
  categoryName: string | null;
  groupName?: string | null;
  onClick: () => void;
  disabled?: boolean;
}) {
  const label = categoryName ?? 'Välj kategori';
  const full = groupName && categoryName ? `${groupName} → ${categoryName}` : label;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={categoryName ? `Kategori: ${full}. Ändra kategori` : 'Välj kategori'}
      title={full}
      className={cn(
        // Visually compact; the ::after expands the hit area toward 44px.
        'relative inline-flex h-7 max-w-full min-w-0 items-center gap-1 rounded-full border px-2.5 text-xs font-medium outline-none transition-colors after:absolute after:-inset-y-2 after:inset-x-0 focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50',
        categoryName
          ? 'border-border bg-background text-foreground hover:bg-muted'
          : 'border-dashed border-warning/50 bg-warning-soft text-warning hover:border-warning',
      )}
    >
      <span className="truncate">{label}</span>
      <ChevronDownIcon aria-hidden className="size-3 shrink-0 opacity-60" />
    </button>
  );
}

/**
 * One purchase: description (tap for details), category chip (tap to change),
 * muted tags and the amount on the right.
 */
export function PurchaseRow({
  row,
  showDate,
  onOpen,
  onPickCategory,
}: {
  row: PurchaseItem;
  /** Rows outside a date group show their own date */
  showDate: boolean;
  onOpen: () => void;
  onPickCategory: () => void;
}) {
  const muted = isMuted(row);
  return (
    <li className="relative flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/40">
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            'block w-full truncate text-left text-[15px] leading-6 font-medium outline-none after:absolute after:inset-0 after:rounded-[inherit] focus-visible:after:ring-3 focus-visible:after:ring-ring/50 focus-visible:after:ring-inset',
            muted ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {row.description}
        </button>
        <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
          <div className="relative z-10 min-w-0 max-w-full">
            <CategoryChip
              categoryName={row.categoryName}
              groupName={row.groupName}
              onClick={onPickCategory}
            />
          </div>
          <PurchaseTags row={row} />
          {(showDate || row.accountName) && (
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {showDate && shortDayLabel(row.date)}
              {showDate && row.accountName && <span className="hidden sm:inline"> · </span>}
              {row.accountName && <span className="hidden sm:inline">{row.accountName}</span>}
            </span>
          )}
        </div>
      </div>
      <span
        className={cn(
          'shrink-0 pt-px text-right text-[15px] leading-6 font-medium whitespace-nowrap tabular-nums',
          amountClass(row),
          row.isExcluded && 'line-through decoration-muted-foreground/50',
        )}
      >
        {formatAmount(row.amount)}
      </span>
    </li>
  );
}
